import React, { useState } from 'react';
import { Box, Button, Divider, Paper, Step, StepButton, Stepper, Typography } from '@mui/material';

import { canAccessInsertionFileUploader } from '@paragon/login-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getProfileIsClosed } from '../../../../../library/profiles/profileManipulation';
import { cancelInsertion } from '../../../../../store/actions/insertActions';
import {
  commitInsertComponentDesign,
  setComponentInsertionStage,
} from '../../../../../store/actions/insertComponentDesignActions';
import { componentInsertionStages } from '../../../../../store/slices/applicationSlice';
import { ConfirmCancel } from '../../../../dialogs/ConfirmCancel';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import InsertionFileUploader from '../../../../misc/InsertionFileUploader';
import { ShapeDetails } from './shape-details/ShapeDetails';
import { ShapeChooser } from './ShapeChooser';
import { FeaturePatternTrussStructuralDetails } from './structural-details/FeaturePatternTrussStructuralDetails';
import { ProfileTrussStructuralDetails } from './structural-details/ProfileTrussStructuralDetails';

const stepLabels = ['Basic Shape', 'Shape Details', 'Structural Details'];

export function ComponentDesignInsertionSubPanel() {
  const dispatch = useAppDispatch();
  const canUploadFiles = useAppSelector(canAccessInsertionFileUploader);

  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);

  const insertingObjectInfo = useAppSelector((state) => state.application.insertingObjectInfo);
  if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Expected to be inserting a component');

  const { loadedInfo, loadingInfo, designSettings, definitionType, stage } = insertingObjectInfo;
  const { isLoading } = loadingInfo;

  const areDesignSettingsComplete =
    designSettings.definitionType === 'Profile'
      ? getProfileIsClosed(designSettings.profile)
      : designSettings.definitionType === 'FeaturePattern'
        ? true
        : assertExhaustiveSwitch(designSettings);

  const stageIndex = componentInsertionStages.indexOf(stage);
  const onLastStep = stageIndex === componentInsertionStages.length - 1;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          overflowX: 'hidden',
          overflowY: 'auto',
          padding: 3,
          borderRadius: 0,
          marginBottom: 'auto',
          height: '100%',
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography align="center" variant="h6">
            Component Insertion
          </Typography>

          <Stepper nonLinear activeStep={stageIndex} sx={{ py: 1 }}>
            {stepLabels.map((label, index) => (
              <Step key={label}>
                <StepButton onClick={() => void dispatch(setComponentInsertionStage(componentInsertionStages[index]))}>
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>

          <Divider sx={{ mb: 2 }} />

          {stage === 'ChoosingShape' ? (
            <ShapeChooser />
          ) : stage === 'ShapeDetails' ? (
            <ShapeDetails />
          ) : stage === 'StructuralDetails' ? (
            definitionType === 'Profile' ? (
              <ProfileTrussStructuralDetails />
            ) : definitionType === 'FeaturePattern' ? (
              <FeaturePatternTrussStructuralDetails forInsertionComponent />
            ) : definitionType === 'Wall' ? null : (
              assertExhaustiveSwitch(definitionType)
            )
          ) : null}
        </Box>
      </Paper>
      <Paper elevation={1} sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', mt: 2 }}>
          {canUploadFiles && <InsertionFileUploader />}
          <Box sx={{ flex: '1 1 auto' }} />

          <Button sx={{ m: 1 }} variant="outlined" onClick={() => void setCancelDialogOpen(true)}>
            Cancel
          </Button>
          <Button
            disabled={!loadedInfo.isLoaded || isLoading || !areDesignSettingsComplete}
            sx={{ m: 1 }}
            variant="contained"
            onClick={
              onLastStep
                ? () => void dispatch(commitInsertComponentDesign())
                : () => void dispatch(setComponentInsertionStage(componentInsertionStages[stageIndex + 1]))
            }
          >
            {onLastStep ? 'Insert' : 'Next'}
          </Button>
        </Box>
        {cancelDialogOpen ? (
          <ConfirmCancel
            onClose={(confirmed) => {
              setCancelDialogOpen(false);

              if (confirmed) {
                dispatch(cancelInsertion());
              }
            }}
          />
        ) : null}
      </Paper>
    </>
  );
}
